import React from "react";
import { useStagingPreview } from "./context";
import { readProjectName, readSlot } from "./session";

/**
 * Sticky blue banner that sits above the storefront header while the user is
 * previewing a staging cycle. Non-dismissible (the only way out is the "Back
 * to live" button). Hidden entirely when not in staging mode.
 *
 * <p>Anatomy (left → right):
 * <ul>
 *   <li><strong>ΠΕΡΙΒΑΛΛΟΝ ΔΟΚΙΜΩΝ</strong> — primary mode label, Greek,
 *       bold all-caps. Same wording as before so muscle memory survives.
 *   <li><strong>Context chip</strong> — translucent pill showing
 *       {@code Project N} when the session is tied to one of the five
 *       pre-staging project slots (n-projects multi-tenant feature), or
 *       {@code Staging} when previewing the plain staging tier with no slot.
 *       Answers "preview of what?" at a glance.
 *   <li><strong>Explanatory line</strong> — muted Greek copy explaining the
 *       banner exists because pre-production data is being shown. Kept so
 *       first-time reviewers (marketing, content) understand the context
 *       without having to ask.
 *   <li><strong>Επιστροφή στη live</strong> — outlined right-side button.
 * </ul>
 *
 * <p>Copy is in Greek — germanos.gr is a Greek storefront and reviewers
 * (marketing, merchandising, content editors) read Greek natively.
 *
 * <p>Styling uses inline styles + a small class set rather than Tailwind so it
 * renders identically whether the page's CSS has loaded or not — the banner is
 * a trust signal and mustn't flash un-styled on slow connections. The blue
 * gradient (sky-500 → blue-600) matches the admin UI's "working" blue family
 * (cf. {@code StagingHero} in the admin Projects dashboard) but at deeper
 * chroma because this banner is uninvited chrome at the top of the viewport
 * and needs to earn its space without the warning-yellow it used to wear.
 *
 * <p>Privacy: the staging cycle id is deliberately NOT rendered anywhere in
 * the banner. Screenshots and screen-shares of the preview would otherwise
 * expose the full preview URL to recipients who are not entitled to it.
 */
export function StagingPreviewBanner() {
  const { isStaging, cycleId, exitStaging } = useStagingPreview();

  if (!isStaging || !cycleId) return null;

  // Project slot (1..5) when the session was opened via /staging?slot=N from
  // the Projects dashboard; null when previewing plain staging. Read directly
  // from sessionStorage on each render — value only changes at session entry,
  // and the banner only renders after hydration so SSR can't see a stale read.
  const slot = readSlot();
  // Project name was added to the URL by admin-bff and persisted by
  // installSlotBootstrap. Falls back to null when previewing plain staging
  // or when the URL didn't include &project= (older admin-bff, or a
  // direct-link preview that bypassed the staging hub).
  const projectName = slot !== null ? readProjectName() : null;
  // Primary label: 'Pre-Staging — <ProjectName>' when inside a project,
  // 'Staging' when previewing the plain staging tier, 'Project N' as a
  // graceful fallback when the slot is set but the name isn't yet
  // persisted (e.g. older admin-bff). The slot pill renders separately
  // beside this label.
  const primaryLabel = (() => {
    if (slot === null) return "Staging";
    if (projectName && projectName.length > 0) return `Pre-Staging — ${projectName}`;
    return `Project ${slot}`;
  })();

  return (
    <>
      {/* Inline @media overrides — additive to the inline styles below. Lives
          in a <style> block (not Tailwind, not an external sheet) so it parses
          with the HTML payload and preserves this banner's "no-CSS-flash"
          trust-signal contract. Each rule uses !important so it can override
          the matching inline style attribute on the same element. Desktop is
          untouched; the @media query only applies at ≤640px (mobile / narrow
          tablet) where the banner used to wrap onto multiple rows and stack
          tall. After these overrides, the essential items fit on one row at
          iPhone-class widths — secondary explanatory text drops, fonts shrink
          one notch, padding/gap tighten. */}
      <style>{`
        @media (max-width: 640px) {
          .grm-staging-banner__row {
            padding: 6px 12px !important;
            gap: 8px !important;
          }
          .grm-staging-banner__primary {
            font-size: 12px !important;
            letter-spacing: 0.01em !important;
          }
          .grm-staging-banner__chip {
            font-size: 10px !important;
            padding: 1px 7px !important;
          }
          .grm-staging-banner__secondary {
            display: none !important;
          }
          .grm-staging-banner__exit {
            padding: 4px 9px !important;
            font-size: 11px !important;
            /* On mobile move the exit CTA to the left edge: order:-1 places it
               first visually inside the flex row, margin-left:0 cancels the
               auto-margin that pushes it right on desktop. Single focusable
               element in the banner so the visual≠DOM-order divergence has no
               keyboard/SR consequences. */
            order: -1 !important;
            margin-left: 0 !important;
          }
        }
      `}</style>
      <div
        role="status"
        aria-live="polite"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 9999,
          background: "linear-gradient(135deg, #1e40af 0%, #172554 100%)",
          color: "#ffffff",
          borderBottom: "2px dashed #facc15",
          fontSize: "14px",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <div
          className="grm-staging-banner__row"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "8px 16px",
            maxWidth: "1600px",
            margin: "0 auto",
            flexWrap: "wrap",
          }}
        >
          <span
            className="grm-staging-banner__primary"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            {/* Inline SVG gear (Lucide "settings" shape, simplified). Inline
                rather than icon-library import so it renders identically before
                CSS / JS bundles load — same constraint that keeps this banner
                on inline styles. stroke=currentColor inherits the white text
                color, so a future palette tweak only needs to change one place. */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{ flexShrink: 0 }}
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            ΠΕΡΙΒΑΛΛΟΝ ΔΟΚΙΜΩΝ
          </span>
          <span
            className="grm-staging-banner__chip"
            style={{
              backgroundColor: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.32)",
              borderRadius: "9999px",
              padding: "2px 10px",
              fontSize: "12px",
              fontWeight: 600,
              color: "#ffffff",
              maxWidth: "320px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            // Tooltip carries the DB name when in a slot (germanos_pre_staging_NN)
            // so the slot identifier is still discoverable on hover — just not
            // shown as a separate pill, which would have been visual noise for
            // the reviewer audience who don't care which slot # holds the data.
            title={
              slot !== null
                ? `${primaryLabel} · germanos_pre_staging_${String(slot).padStart(2, "0")}`
                : primaryLabel
            }
          >
            {primaryLabel}
          </span>
          <span
            className="grm-staging-banner__secondary"
            style={{ color: "#dbeafe", fontSize: "13px" }}
          >
            Βλέπετε δεδομένα προ-παραγωγής.
          </span>
          {/* Wrapper exists so we can give the FLEX-ITEM responsibility (the
              right-push on desktop, the row-break on mobile) to something
              other than the button itself. Letting the wrapper take
              flex-basis:100% on mobile forces a new row while the button
              inside renders at natural width — that's how we get the CTA
              "bottom-left of the banner" placement without stretching the
              button edge-to-edge. */}
          <div
            className="grm-staging-banner__exit-wrap"
            style={{ marginLeft: "auto" }}
          >
            <button
              className="grm-staging-banner__exit"
              type="button"
              onClick={exitStaging}
              style={{
                backgroundColor: "rgba(255,255,255,0.16)",
                color: "#ffffff",
                // No explicit `border` — the translucent fill already separates
                // the button from the banner gradient. The browser still draws
                // its native focus ring for keyboard users (a11y intact); the
                // always-on white outline was just visual noise.
                border: "none",
                borderRadius: "6px",
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
              }}
              aria-label="Επιστροφή στην κανονική προβολή"
            >
              Επιστροφή στο Live →
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
